/**
 * Shift Tic Tac Toe - Move Validator
 * Additional validation utilities for game moves
 */

import {
  getValidPlacements,
  getValidShifts,
  getValidShiftDestinations,
  checkWinner,
  WIN_LINES,
} from './gameLogic';

/**
 * Validate a placement move
 * @param {Object} state - Current game state
 * @param {number} position - Position to place
 * @param {string} player - Player making the move
 * @returns {Object} { valid: boolean, error?: string }
 */
export function validatePlacement(state, position, player) {
  const { board, phase, currentPlayer, piecesPlaced, maxPieces, winner, isDraw } = state;
  
  if (winner) {
    return { valid: false, error: 'Game is over - there is a winner' };
  }
  
  if (isDraw) {
    return { valid: false, error: 'Game is over - draw' };
  }
  
  if (phase !== 'placement') {
    return { valid: false, error: 'Not in placement phase' };
  }
  
  if (player !== currentPlayer) {
    return { valid: false, error: 'Not your turn' };
  }
  
  if (position < 0 || position > 8) {
    return { valid: false, error: 'Invalid position' };
  }
  
  if (board[position] !== null) {
    return { valid: false, error: 'Cell is occupied' };
  }
  
  if (piecesPlaced[player] >= maxPieces[player]) {
    return { valid: false, error: 'No more pieces to place' };
  }
  
  return { valid: true };
}

/**
 * Validate a shift move
 * @param {Object} state - Current game state
 * @param {number} from - Source position
 * @param {number} to - Destination position
 * @param {string} player - Player making the move
 * @returns {Object} { valid: boolean, error?: string }
 */
export function validateShift(state, from, to, player) {
  const { board, phase, currentPlayer, winner, isDraw } = state;
  
  if (winner) {
    return { valid: false, error: 'Game is over - there is a winner' };
  }
  
  if (isDraw) {
    return { valid: false, error: 'Game is over - draw' };
  }
  
  if (phase !== 'shift') {
    return { valid: false, error: 'Not in shift phase' };
  }
  
  if (player !== currentPlayer) {
    return { valid: false, error: 'Not your turn' };
  }
  
  if (from < 0 || from > 8 || to < 0 || to > 8) {
    return { valid: false, error: 'Invalid position' };
  }
  
  if (board[from] === null) {
    return { valid: false, error: 'No piece to move' };
  }
  
  if (board[from] !== player) {
    return { valid: false, error: 'Not your piece' };
  }

  if (board[to] !== null) {
    return { valid: false, error: 'Destination is occupied' };
  }
  
  return { valid: true };
}

/**
 * Validate piece selection for shift
 * @param {Object} state - Current game state
 * @param {number} position - Position of piece to select
 * @param {string} player - Player making the selection
 * @returns {Object} { valid: boolean, error?: string, destinations?: number[] }
 */
export function validateSelection(state, position, player) {
  const { board, phase, currentPlayer, allowDiagonal, winner, isDraw } = state;
  
  if (winner || isDraw) {
    return { valid: false, error: 'Game is over' };
  }
  
  if (phase !== 'shift') {
    return { valid: false, error: 'Not in shift phase' };
  }
  
  if (player !== currentPlayer) {
    return { valid: false, error: 'Not your turn' };
  }
  
  if (board[position] !== player) {
    return { valid: false, error: 'Not your piece' };
  }
  
  const destinations = getValidShiftDestinations(board, position);
  if (destinations.length === 0) {
    return { valid: false, error: 'No empty cells to move to' };
  }
  
  return { valid: true, destinations };
}

/**
 * Check if a move would result in a win
 * @param {Array} board - Current board state
 * @param {number} position - Position to check
 * @param {string} player - Player to check for
 * @returns {boolean}
 */
export function wouldWin(board, position, player) {
  const testBoard = [...board];
  testBoard[position] = player;
  const result = checkWinner(testBoard);
  return result && result.winner === player;
}

/**
 * Check if a shift would result in a win
 * @param {Array} board - Current board state
 * @param {number} from - Source position
 * @param {number} to - Destination position
 * @param {string} player - Player making the move
 * @returns {boolean}
 */
export function shiftWouldWin(board, from, to, forPlayer) {
  const piece = board[from];
  if (!piece) return false;
  const testBoard = [...board];
  testBoard[from] = null;
  testBoard[to] = piece;
  const result = checkWinner(testBoard);
  return result && result.winner === forPlayer;
}

/**
 * Find winning move for player in placement phase
 * @param {Array} board - Current board state
 * @param {string} player - Player to check
 * @returns {number|null} Position that wins, or null
 */
export function findWinningPlacement(board, player) {
  const emptyPositions = board.reduce((acc, cell, i) => {
    if (cell === null) acc.push(i);
    return acc;
  }, []);
  
  for (const pos of emptyPositions) {
    if (wouldWin(board, pos, player)) {
      return pos;
    }
  }
  
  return null;
}

/**
 * Find winning shift for player
 * @param {Object} state - Game state
 * @param {string} player - Player to check
 * @returns {Object|null} { from, to } that wins, or null
 */
export function findWinningShift(state, player) {
  const shifts = getValidShifts(state);
  
  for (const { from, to } of shifts) {
    if (shiftWouldWin(state.board, from, to, player)) {
      return { from, to };
    }
  }
  
  return null;
}

/**
 * Find blocking move (prevent opponent from winning)
 * @param {Array} board - Current board state
 * @param {string} opponent - Opponent player
 * @returns {number|null} Position that blocks, or null
 */
export function findBlockingPlacement(board, opponent) {
  return findWinningPlacement(board, opponent);
}

/**
 * Find blocking shift (prevent opponent from winning next turn)
 * @param {Object} state - Game state
 * @param {string} player - Current player
 * @returns {Object|null} { from, to } that blocks, or null
 */
export function findBlockingShift(state, player) {
  const opponent = player === 'X' ? 'O' : 'X';
  const myShifts = getValidShifts(state);
  
  // Check if opponent can win next turn if we don't block
  const tempState = { ...state, currentPlayer: opponent };
  const opponentWinningShift = findWinningShift(tempState, opponent);
  
  if (!opponentWinningShift) return null;
  
  // Find a move that blocks the winning destination
  for (const { from, to } of myShifts) {
    if (to === opponentWinningShift.to) {
      return { from, to };
    }
  }
  
  return null;
}

/**
 * Count pieces that can form a line
 * @param {Array} board - Current board state
 * @param {string} player - Player to check
 * @returns {Object} { twoInRow: number, potentialLines: Array }
 */
export function analyzePosition(board, player) {
  let twoInRow = 0;
  const potentialLines = [];
  
  for (const line of WIN_LINES) {
    const cells = line.map(i => board[i]);
    const playerCount = cells.filter(c => c === player).length;
    const emptyCount = cells.filter(c => c === null).length;
    
    if (playerCount === 2 && emptyCount === 1) {
      twoInRow++;
      potentialLines.push({
        line,
        emptyIndex: line[cells.indexOf(null)],
      });
    }
  }
  
  return { twoInRow, potentialLines };
}

/**
 * Get all pieces belonging to a player
 * @param {Array} board - Current board state
 * @param {string} player - Player to check
 * @returns {number[]} Array of positions
 */
export function getPlayerPieces(board, player) {
  return board.reduce((acc, cell, index) => {
    if (cell === player) acc.push(index);
    return acc;
  }, []);
}

/**
 * Check if the game is still playable
 * @param {Object} state - Game state
 * @returns {boolean}
 */
export function isGameActive(state) {
  return !state.winner && !state.isDraw;
}

/**
 * Get remaining pieces for a player
 * @param {Object} state - Game state
 * @param {string} player - Player to check
 * @returns {number}
 */
export function getRemainingPieces(state, player) {
  return state.maxPieces[player] - state.piecesPlaced[player];
}

export default {
  validatePlacement,
  validateShift,
  validateSelection,
  wouldWin,
  shiftWouldWin,
  findWinningPlacement,
  findWinningShift,
  findBlockingPlacement,
  findBlockingShift,
  analyzePosition,
  getPlayerPieces,
  isGameActive,
  getRemainingPieces,
};
